import SearchInput from '../../../../../commons/SearchInput/SearchInput';
import { ReactComponent as SearchIcon } from '../../../../../../assets/vectors/search.svg';
import Button from '../../../../../designSystem/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useConsultations } from '../../../../../../hooks/fetchers/useConsultations/useConsultations';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { SearchConsultationsQueryResponse } from '../../../../../../types/api/endpoints/consultations/searchConsultations.types';
import { SearchConsultationsQuery } from './DashboardConsultationsTable.types';
import {
  SEARCH_CONSULTATIONS_QUERY_KEY,
  searchConsultationsQuery,
} from './DashboardConsultationsTable.query';
import { useMergedStatesFromFetchers } from '../../../../../../hooks/useMergedStatesFromFetchers/useMergedStatesFromFetchers';
import Loading from '../../../../../commons/Loading/Loading';
import Pagination from '../../../../../commons/Pagination/Pagination';
import Empty from '../../../../../commons/Empty/Empty';
import { getChipContentByStepId } from '../../../../../../utils/helpers/consultation/getChipContentByStepId';
import { ButtonColor } from '../../../../../designSystem/Button/Button.types';
import RemoveConsultationModal from '../../DashboardHistoryPUB/DashboardHistoryPUBSummary/RemoveConsultationModal/RemoveConsultationModal';
import DuplicateConsultationModal from '../../DashboardHistoryPUB/DashboardHistoryPUBSummary/DuplicateConsultationModal/DuplicateConsultationModal';
import moment from 'moment';
import useIsMobile from '../../../../../../hooks/useIsMobile/useIsMobile';

export default () => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const isMobile = useIsMobile();

  const [isRemoveConsultModalOpen, setRemoveConsultModalOpen] = useState(false);
  const [isDuplicateConsultModalOpen, setDuplicateConsultModalOpen] = useState(false);

  const [selectedConsultationId, setSelectedConsultationId] = useState<number>();

  const [searchValue, setSearchValue] = useState<string>();
  const [searchResults, setSearchResults] = useState<SearchConsultationsQueryResponse>();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(25);

  const {
    data: consultations,
    refetch: refetchConsultations,
    isLoading: isConsultationsLoading,
    isFetching: isConsultationsFetching,
    error: consultationsError,
  } = useConsultations({ page: currentPage, perPage: itemsPerPage });

  const {
    mutate: searchMutate,
    isLoading: isSearchLoading,
    error: searchError,
  } = useMutation<Awaited<SearchConsultationsQueryResponse>, Error, SearchConsultationsQuery>(
    (data) => searchConsultationsQuery(data),
    {
      mutationKey: SEARCH_CONSULTATIONS_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: (response) => {
        setSearchResults(response);
      },
      onError: (error) => {
        if (error.message === 'Aucune consultation trouvée') {
          setSearchResults({ results: [], total: 0 });
        }
      },
    }
  );

  useEffect(() => {
    if (searchResults) {
      searchMutate({
        search: searchValue || '',
        params: {
          page: currentPage,
          perPage: itemsPerPage,
        },
      });
    } else {
      queryClient.cancelMutations();
      refetchConsultations();
    }
  }, [currentPage]);

  const numberOfPages = useMemo(() => {
    if (searchResults) {
      // @ts-ignore
      return parseInt(searchResults.total / itemsPerPage) + 1;
    } else {
      // @ts-ignore
      return parseInt(consultations?.total / itemsPerPage) + 1;
    }
  }, [consultations, searchResults, itemsPerPage]);

  const onChangeSearchText = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      setCurrentPage(1);
      if (value.length === 0) {
        setSearchResults(undefined);
      } else {
        searchMutate({
          search: value,
          params: {
            page: currentPage,
            perPage: itemsPerPage,
          },
        });
      }
    },
    [searchMutate, currentPage, itemsPerPage]
  );

  const openRemoveModal = (consultationId: number) => {
    setSelectedConsultationId(consultationId);
    setRemoveConsultModalOpen(true);
  };

  const openDuplicateModal = (consultationId: number) => {
    setSelectedConsultationId(consultationId);
    setDuplicateConsultModalOpen(true);
  };

  const { isLoading, error } = useMergedStatesFromFetchers({
    loadings: [isConsultationsLoading, isConsultationsFetching, isSearchLoading],
    errors: [consultationsError, searchError],
  });

  return (
    <>
      <div className="dashboard-commerciaux">
        <div className="dashboard-commerciaux__header">
          <SearchInput
            placeholder="Chercher une consultation..."
            startIcon={SearchIcon}
            onChange={onChangeSearchText}
            handleSelectedSuggestion={(suggestion) => console.log(suggestion)}
          />
          {/* <Link to="/dashboard/nouvelle_consultation">
            <Button>
              {isMobile
                ? 'Nouvelle'
                : 'Nouvelle consultation'
              }
            </Button>
          </Link> */}
          <Button onClick={() => {
            // @ts-ignore
            window.location = "/portal/dashboard/nouvelle_consultation"
          }}>
            {isMobile
              ? 'Nouvelle'
              : 'Nouvelle consultation'
            }
          </Button>
        </div>
        <table className="dashboard__table dashboard-commerciaux__table">
          {isLoading || error ? (
            error ? (
              <Empty>Aucune consultation trouvée</Empty>
            ) : (
              <Loading />
            )
          ) : (
            <>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Réf.</th>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Date de création</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {searchResults &&
                  searchResults.results?.map((consultation, index) => {
                    const { id, ref, title, id_step, type, end, created_at } = consultation;
                    const { label } = getChipContentByStepId(consultation);
                    return (
                      <tr
                        key={index}
                        onClick={() =>
                          id_step < 4
                            ? navigate(`/dashboard/nouvelle_consultation/${id}`)
                            : navigate(`/dashboard/consultations/${id}`)
                        }
                        className={
                          id_step < 4
                            ? 'dashboard-commerciaux__table-tr--clickable'
                            : 'dashboard-commerciaux__table-tr--clickable'
                        }>
                        <td>
                          <strong>#{id}</strong>
                        </td>
                        <td>{ref || ''}</td>
                        <td>{title}</td>
                        <td>{type}</td>
                        <td className="text-primary">{label}</td>
                        <td>{moment(created_at).format('DD/MM/YYYY')}</td>
                        <td
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            columnGap: 'var(--spacing-s)',
                          }}>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDuplicateModal(id);
                            }}
                            color={ButtonColor.Contrast}>
                            Dupliquer
                          </Button>
                          {((id_step <= 4 || moment().isBefore(end)) && label !== 'En cours') && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openRemoveModal(id);
                              }}>
                              Supprimer
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                {!searchResults &&
                  consultations &&
                  consultations.results.length > 0 &&
                  consultations?.results.map((consultation, index) => {
                    const { id, ref, title, id_step, type, end, created_at } = consultation;
                    const { label } = getChipContentByStepId(consultation);
                    return (
                      <tr
                        key={index}
                        onClick={() =>
                          id_step < 4
                            ? navigate(`/dashboard/nouvelle_consultation/${id}`)
                            : navigate(`/dashboard/consultations/${id}`)
                        }
                        className={
                          id_step < 4
                            ? 'dashboard-commerciaux__table-tr--clickable'
                            : 'dashboard-commerciaux__table-tr--clickable'
                        }>
                        <td>
                          <strong>#{id}</strong>
                        </td>
                        <td>{ref || ''}</td>
                        <td>{title}</td>
                        <td>{type}</td>
                        <td className="text-primary">{label}</td>
                        <td>{moment(created_at).format('DD/MM/YYYY')}</td>
                        <td
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            columnGap: 'var(--spacing-s)',
                          }}>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDuplicateModal(id);
                            }}
                            color={ButtonColor.Contrast}>
                            Dupliquer
                          </Button>
                          {((id_step <= 4 || moment().isBefore(end)) && label !== 'En cours') && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openRemoveModal(id);
                              }}>
                              Supprimer
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </>
          )}
        </table>
      </div>
      {!isLoading && !error && <Pagination {...{ currentPage, setCurrentPage, numberOfPages }} />}
      {selectedConsultationId && (
        <RemoveConsultationModal
          consultationId={selectedConsultationId}
          isOpen={isRemoveConsultModalOpen}
          setOpen={setRemoveConsultModalOpen}
          onClose={() => setSelectedConsultationId(undefined)}
          onSuccess={() => navigate(0)}
          closeOnClickOutside
        />
      )}
      {selectedConsultationId && (
        <DuplicateConsultationModal
          consultationId={selectedConsultationId}
          isOpen={isDuplicateConsultModalOpen}
          setOpen={setDuplicateConsultModalOpen}
          onClose={() => setSelectedConsultationId(undefined)}
          closeOnClickOutside
        />
      )}
    </>
  );
};
